import React from 'react';
import { useTranslation } from 'react-i18next';
import InfoIcon from './InfoIcon.js';
import Tooltip from './Tooltip.js';

function EventDetailsForm({ formData, updateFormData }) {
  const { t } = useTranslation();
  const handleChange = (e) => {
    if (e.target.name === 'prohibitedCategories') {
      const { value, checked } = e.target;
      const currentCategories = formData.prohibitedCategories || [];
      if (checked) {
        updateFormData({ prohibitedCategories: [...currentCategories, value] });
      } else {
        updateFormData({ prohibitedCategories: currentCategories.filter(category => category !== value) });
      }
    } else {
      updateFormData({ [e.target.name]: e.target.value });
    }
  };

  const handleEventTypeChange = (eventType) => {
    updateFormData({ eventType });
  };

  return (
    <form className="space-y-6">
      {/* Event Type Section */}
      <div className="bg-white border border-gray-300 rounded-lg p-6 shadow-sm">
        <div className="flex items-center mb-4">
          <h3 className="text-xl font-bold text-gray-800">{t('eventSetup.details.eventType.title')}</h3>
          <div className="ml-2">
            <Tooltip text={t('eventSetup.details.eventType.tooltip')}>
              <InfoIcon />
            </Tooltip>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div
            onClick={() => handleEventTypeChange('kris-kindle')}
            className={`cursor-pointer p-6 border-2 rounded-lg text-center transition-all ${
              formData.eventType === 'kris-kindle'
                ? 'bg-blue-500 text-white border-blue-600 shadow-md'
                : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400 hover:shadow-sm'
            }`}
          >
            <h3 className="text-lg font-bold mb-2">{t('eventSetup.details.eventType.krisKindle.name')}</h3>
            <p className="text-sm">{t('eventSetup.details.eventType.krisKindle.description')}</p>
          </div>
          <div
            onClick={() => handleEventTypeChange('secret-santa')}
            className={`cursor-pointer p-6 border-2 rounded-lg text-center transition-all ${
              formData.eventType === 'secret-santa'
                ? 'bg-blue-500 text-white border-blue-600 shadow-md'
                : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400 hover:shadow-sm'
            }`}
          >
            <h3 className="text-lg font-bold mb-2">{t('eventSetup.details.eventType.secretSanta.name')}</h3>
            <p className="text-sm">{t('eventSetup.details.eventType.secretSanta.description')}</p>
          </div>
        </div>
      </div>
      {/* Basic Information Section */}
      <div className="bg-white border border-gray-300 rounded-lg p-6 shadow-sm">
        <h3 className="text-xl font-bold text-gray-800 mb-4">{t('eventSetup.details.basicInfo.title')}</h3>
        <div className="space-y-4">
          <div>
            <label htmlFor="eventName" className="block text-sm font-semibold text-gray-700 mb-2">{t('eventSetup.details.basicInfo.eventName.label')}</label>
            <input
              type="text"
              id="eventName"
              name="eventName"
              value={formData.eventName}
              onChange={handleChange}
              placeholder={t('eventSetup.details.basicInfo.eventName.placeholder')}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
            />
          </div>
          <div>
            <label htmlFor="eventDate" className="block text-sm font-semibold text-gray-700 mb-2">{t('eventSetup.details.basicInfo.eventDate.label')}</label>
            <input
              type="date"
              id="eventDate"
              name="eventDate"
              value={formData.eventDate}
              onChange={handleChange}
              className="w-full min-w-0 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
            />
          </div>
        </div>
      </div>
      {/* Budget Section */}
      <div className="bg-white border border-gray-300 rounded-lg p-6 shadow-sm">
        <h3 className="text-xl font-bold text-gray-800 mb-4">{t('eventSetup.details.budget.title')}</h3>
        {formData.eventType === 'kris-kindle' ? (
          <div className="space-y-4">
            <div className="flex items-center p-3 bg-gray-50 rounded-md">
              <input
                type="checkbox"
                id="sameBudgetForAll"
                name="sameBudgetForAll"
                checked={formData.sameBudgetForAll}
                onChange={(e) => updateFormData({ sameBudgetForAll: e.target.checked })}
                className="w-4 h-4 text-blue-600 mr-3"
              />
              <label htmlFor="sameBudgetForAll" className="text-sm font-medium text-gray-700">{t('eventSetup.details.budget.sameBudgetForAll')}</label>
            </div>
            {formData.sameBudgetForAll ? (
              <div>
                <label htmlFor="budget" className="block text-sm font-semibold text-gray-700 mb-2">{t('eventSetup.details.budget.budgetAmount')}</label>
                <input
                  type="number"
                  id="budget"
                  name="budget"
                  value={formData.budget}
                  onChange={handleChange}
                  placeholder={t('eventSetup.details.budget.placeholder')}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                />
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-gray-600 mb-3">{t('eventSetup.details.budget.setBudgetByAge')}</p>
                {['0-3', '4-7', '8-12', '13-17', 'Adult'].map(type => (
                  <div key={type} className="flex items-center gap-4">
                    <label htmlFor={`budget-${type}`} className="w-24 text-sm font-medium text-gray-700">{t(`ageGroups.${type}`)}</label>
                    <div className="flex-1">
                      <input
                        type="number"
                        id={`budget-${type}`}
                        name={`budget-${type}`}
                        value={formData.budgetsByType?.[type] || ''}
                        onChange={(e) => {
                          const newBudgets = { ...(formData.budgetsByType || {}), [type]: e.target.value };
                          updateFormData({ budgetsByType: newBudgets });
                        }}
                        placeholder={t('eventSetup.details.budget.placeholder')}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div>
            <label htmlFor="budget" className="block text-sm font-semibold text-gray-700 mb-2">{t('eventSetup.details.budget.budgetAmount')}</label>
            <input
              type="number"
              id="budget"
              name="budget"
              value={formData.budget}
              onChange={handleChange}
              placeholder="50"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
            />
          </div>
        )}
      </div>
      {/* Number of Gifts Section (Kris Kindle only) */}
      {formData.eventType === 'kris-kindle' && (
        <div className="bg-white border border-gray-300 rounded-lg p-6 shadow-sm">
          <h3 className="text-xl font-bold text-gray-800 mb-4">{t('eventSetup.details.numberOfGifts.title')}</h3>
          <div className="space-y-4">
            <div className="flex items-center p-3 bg-gray-50 rounded-md">
              <input
                type="checkbox"
                id="sameGiftNumberForAll"
                name="sameGiftNumberForAll"
                checked={formData.sameGiftNumberForAll}
                onChange={(e) => updateFormData({ sameGiftNumberForAll: e.target.checked })}
                className="w-4 h-4 text-blue-600 mr-3"
              />
              <label htmlFor="sameGiftNumberForAll" className="text-sm font-medium text-gray-700">{t('eventSetup.details.numberOfGifts.sameNumberForAll')}</label>
            </div>
            {formData.sameGiftNumberForAll ? (
              <div>
                <label htmlFor="numberOfGifts" className="block text-sm font-semibold text-gray-700 mb-2">{t('eventSetup.details.numberOfGifts.numberOfGifts')}</label>
                <input
                  type="number"
                  id="numberOfGifts"
                  name="numberOfGifts"
                  value={formData.numberOfGifts}
                  onChange={handleChange}
                  placeholder={t('eventSetup.details.numberOfGifts.placeholder')}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                />
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-gray-600 mb-3">{t('eventSetup.details.numberOfGifts.setByAge')}</p>
                {['0-3', '4-7', '8-12', '13-17', 'Adult'].map(type => (
                  <div key={type} className="flex items-center gap-4">
                    <label htmlFor={`gifts-${type}`} className="w-24 text-sm font-medium text-gray-700">{t(`ageGroups.${type}`)}</label>
                    <div className="flex-1">
                      <input
                        type="number"
                        id={`gifts-${type}`}
                        name={`gifts-${type}`}
                        value={formData.giftsByType?.[type] || ''}
                        onChange={(e) => {
                          const newGifts = { ...(formData.giftsByType || {}), [type]: e.target.value };
                          updateFormData({ giftsByType: newGifts });
                        }}
                        placeholder={t('eventSetup.details.numberOfGifts.placeholder')}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      {/* Additional Details Section */}
      <div className="bg-white border border-gray-300 rounded-lg p-6 shadow-sm">
        <h3 className="text-xl font-bold text-gray-800 mb-4">{t('eventSetup.details.additionalDetails.title')}</h3>
        <div className="space-y-4">
          <div>
            <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">{t('eventSetup.details.additionalDetails.message.label')}</label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              rows="4"
              placeholder={t('eventSetup.details.additionalDetails.message.placeholder')}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none"
            ></textarea>
          </div>
        </div>
      </div>
      {/* Prohibited Gift Categories (Secret Santa only) */}
      {formData.eventType === 'secret-santa' && (
        <div className="bg-white border border-gray-300 rounded-lg p-6 shadow-sm">
          <h3 className="text-xl font-bold text-gray-800 mb-4">{t('eventSetup.details.prohibitedCategories.title')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[
              { id: 'adultContent', label: t('eventSetup.details.prohibitedCategories.adultContent'), value: 'Adult content' },
              { id: 'alcohol', label: t('eventSetup.details.prohibitedCategories.alcohol'), value: 'Alcohol' },
              { id: 'weapons', label: t('eventSetup.details.prohibitedCategories.weapons'), value: 'Weapons' },
              { id: 'tobacco', label: t('eventSetup.details.prohibitedCategories.tobacco'), value: 'Tobacco' },
              { id: 'perishableFood', label: t('eventSetup.details.prohibitedCategories.perishableFood'), value: 'Perishable food' },
              { id: 'expensiveLuxuryItems', label: t('eventSetup.details.prohibitedCategories.expensiveLuxuryItems'), value: 'Expensive luxury items' },
              { id: 'gagGifts', label: t('eventSetup.details.prohibitedCategories.gagGifts'), value: 'Gag gifts' },
              { id: 'politicalControversial', label: t('eventSetup.details.prohibitedCategories.politicalControversial'), value: 'Political/controversial items' }
            ].map(category => (
              <div key={category.id} className="flex items-center p-3 bg-gray-50 rounded-md hover:bg-gray-100 transition-colors">
                <input
                  type="checkbox"
                  id={category.id}
                  name="prohibitedCategories"
                  value={category.value}
                  onChange={handleChange}
                  checked={formData.prohibitedCategories.includes(category.value)}
                  className="w-4 h-4 text-blue-600 mr-3"
                />
                <label htmlFor={category.id} className="text-sm font-medium text-gray-700 cursor-pointer">{category.label}</label>
              </div>
            ))}
          </div>
        </div>
      )}
    </form>
  );
}

export default EventDetailsForm;
